/* =========================================================================
   LAZ Skincare — routine page (both modes)

     own mode     /pages/routine          the localStorage working copy
     shared mode  /pages/routine?token=   an immutable KV snapshot

   The page is a scaffold of every step in application order, not a flat list.
   Empty steps stay on screen with a link into that step's collection, so the
   shape of a complete routine is visible and the gaps are obvious. Ordering
   across steps is therefore fixed; the up/down controls move an item only
   among its own step's products.

   When the working copy is bound to a saved routine, the engine writes every
   edit through to the account by itself. The Save button becomes a status
   readout for that — Saved / Saving / Retry, with a matching glyph — rather
   than a thing you must remember to press.

   Shared mode never writes to the working copy, and shows only the steps that
   have something in them. The mode-specific DOM is removed rather than
   hidden, so read-only really is read-only.
   ========================================================================= */

(function () {
  "use strict";

  const R = window.LazRoutine;
  if (!R) return;

  const root = document.querySelector("[data-lsc-routine-page]");
  if (!root) return;

  const token = new URLSearchParams(window.location.search).get("token");
  const isShared = Boolean(token);

  /* LSC - ?new=1 is how the saved-routines page asks for an empty builder. It
     is a request, not an instruction: startNewRoutine() still decides whether
     anything is at risk and whether to ask first. Shared views ignore it —
     there is no working copy on screen to replace. */
  const wantsNew =
    !isShared && new URLSearchParams(window.location.search).get("new") === "1";

  /* Taken out of the address bar as soon as it is acted on, so a refresh or a
     shared URL does not ask the question a second time. Only this one
     parameter is dropped; anything else on the URL belongs to someone else. */
  function consumeNewParam() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("new");
      window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    } catch {
      /* An unparseable URL is not worth failing the page over. */
    }
  }

  const isCustomer = root.dataset.customer === "true";
  const myRoutinesUrl = root.dataset.myRoutinesUrl || "/pages/my-routines";
  const defaultTitle = root.dataset.defaultTitle || "My Routine";
  const shopName = root.dataset.shopName || "LAZ Skincare";
  const shopUrl = root.dataset.shopUrl || "https://lazskincare.com";
  const printLogo = root.dataset.printLogo || "";
  const PROMPT_KEY = "laz_routine_prompted";
  const BUSY_LABEL = "Working…";

  /* Drop the other mode's nodes before anything reads the DOM. */
  root
    .querySelectorAll(isShared ? "[data-mode-own]" : "[data-mode-shared]")
    .forEach((node) => node.remove());

  const listEl = root.querySelector("[data-routine-list]");
  const countEl = root.querySelector("[data-routine-count]");
  const titleEl = root.querySelector("[data-routine-title]");
  const toolbarEl = root.querySelector("[data-routine-toolbar]");
  /* LSC - The one toolbar button that survives an empty routine. Held as a
     reference because syncToolbar() compares against it on every render; null
     in shared mode, where the button is pruned and syncToolbar returns before
     it is read. */
  const addProductButton = root.querySelector("[data-routine-add-product]");
  const notesEl = root.querySelector("[data-routine-notes]");
  const errorEl = root.querySelector("[data-shared-error]");
  const errorText = root.querySelector("[data-shared-error-text]");
  const sharePanel = root.querySelector("[data-routine-share-panel]");
  const shareInput = root.querySelector("[data-routine-share-input]");
  /* LSC - The toolbar button that opens the share panel. Held as a reference
     rather than looked up per click, because closing now has to put focus
     back on it and keep its aria-expanded honest. */
  const shareToggle = root.querySelector("[data-routine-share]");
  /* LSC - The QR code sits inside the share panel now; there is no QR panel
     and no second copy of the link to go with it. */
  const qrBlock = root.querySelector("[data-routine-qr-block]");
  const qrCode = root.querySelector("[data-routine-qr-code]");
  const saveButton = root.querySelector("[data-routine-save]");
  const saveLabel = root.querySelector("[data-routine-save-label]");
  const saveIcon = root.querySelector("[data-routine-save-icon]");
  /* LSC - The own-mode intro copy, hidden once the routine has products. Null
     in shared mode, where the other mode's nodes were pruned above and the
     shared intro is a different paragraph that always applies. */
  const introEl = root.querySelector("[data-routine-intro]");
  const noteSection = root.querySelector("[data-routine-note-section]");
  const noteText = root.querySelector("[data-routine-note-text]");
  const noteEdit = root.querySelector("[data-routine-note-edit]");

  /* Step metadata comes from Liquid so the collection URLs survive a handle
     change without a JS edit. */
  let STEPS = [];
  try {
    const raw = root.querySelector("[data-routine-steps]");
    STEPS = JSON.parse(raw.textContent);
  } catch {
    STEPS = R.STEP_CHOICES.map((label) => ({ label, number: "", name: label, url: "" }));
  }
  const stepMeta = new Map(STEPS.map((s) => [s.label, s]));

  let snapshot = null;
  let renderId = 0;

  /* LSC - Send a guest back to the routine they were reading.

     The shared view's "Log in to save" button is a plain link to the login
     page; this is what makes it come back. It has to happen here rather than
     in Liquid, because the thing worth returning to is the ?token= in the
     query string and Liquid cannot read a query string.

     Appending the parameter by hand rather than with URL(): routes
     .account_login_url is a relative path, and `new URL("/account/login")`
     throws without a base. The separator is chosen rather than assumed, so a
     login URL that ever arrives carrying a query of its own does not end up
     with two question marks in it.

     forEach over all of them. There is one today — the toolbar button — but
     this used to be a footer link, and the next time something wants a login
     route it should get the redirect by putting the attribute on, not by
     discovering that only the first match was wired up. */
  root.querySelectorAll("[data-shared-login]").forEach((link) => {
    const base = link.getAttribute("href") || "/account/login";
    const separator = base.indexOf("?") === -1 ? "?" : "&";
    link.href =
      base +
      separator +
      "return_url=" +
      encodeURIComponent(window.location.pathname + window.location.search);
  });

  const currentItems = () => (isShared ? (snapshot && snapshot.items) || [] : R.items());
  const currentName = () => (isShared ? snapshot && snapshot.name : R.read().name);
  const currentNote = () => (isShared ? snapshot && snapshot.note : R.read().note);

  /* ---------------------------------------------------------------------
     icons

     Every other toolbar glyph is rendered by Liquid. These three live here
     because the Save button's icon tracks sync state and has to change after
     load: a bookmark while there is something to save, a check once the
     account has caught up, a retry arrow when it hasn't.
     --------------------------------------------------------------------- */

  const ICONS = {
    save: '<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>',
    saved: '<path d="M20 6L9 17l-5-5"/>',
    retry: '<path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>',
  };

  function iconSvg(paths) {
    return (
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      paths +
      "</svg>"
    );
  }

  /* ---------------------------------------------------------------------
     load
     --------------------------------------------------------------------- */

  async function boot() {
    if (!isShared) {
      R.onChange(scheduleRender);
      R.onSync(updateSaveLabel);
      await render();

      /* LSC - Arrived from the saved-routines page's "New routine" button.
         Awaited behind the first render so the question is asked with the
         routine it concerns visible, not over a loading list. */
      if (wantsNew) {
        consumeNewParam();
        await startNewRoutine({ fromLink: true });
      }
      return;
    }

    try {
      const res = await R.readShared(token);
      snapshot = res.routine || res;
      if (!snapshot || !(snapshot.items || []).length) {
        return fail("This routine is empty.");
      }
      render();
    } catch (err) {
      if (err.status === 404) {
        fail("This routine has expired or the link is no longer valid.");
      } else if (err.status === 429) {
        fail("This link is being opened a lot right now. Try again in a minute.");
      } else {
        fail(err.message);
      }
    }
  }

  function fail(message) {
    listEl.textContent = "";
    listEl.setAttribute("aria-busy", "false");
    toolbarEl.hidden = true;
    if (noteSection) noteSection.hidden = true;
    if (errorEl) errorEl.hidden = false;
    if (errorText) errorText.textContent = message;
  }

  /* ---------------------------------------------------------------------
     rendering
     --------------------------------------------------------------------- */

  /* LSC - Coalesce bursts of mutations into one render.

     A drag that moves a product three places commits as three moveWithinStep
     calls, because that is the one write path that syncs correctly. Each one
     dispatches a change, and subscribing render() directly meant three full
     renders where the renderId guard then threw the first two away — the DOM
     work was done and discarded.

     One frame's delay costs nothing visible (the drag has already finished
     animating) and it absorbs any future burst for free.

     A frame is raced against a short timer rather than trusted on its own:
     requestAnimationFrame does not fire in a background tab, and a routine
     edited in one tab (or by a cross-tab storage event) would otherwise sit
     un-rendered until the tab was looked at again. Whichever lands first wins
     and cancels the other. */
  let renderFrame = 0;
  let renderTimer = 0;

  function scheduleRender() {
    if (renderFrame || renderTimer) return;

    const run = () => {
      window.cancelAnimationFrame(renderFrame);
      window.clearTimeout(renderTimer);
      renderFrame = 0;
      renderTimer = 0;
      render();
    };

    renderFrame = window.requestAnimationFrame(run);
    renderTimer = window.setTimeout(run, 32);
  }

  /* LSC - What the toolbar shows when the routine is empty.

     It used to hide the whole row, which was right when every button in it
     acted on products that were not there: Add all, Share and Print all refuse
     an empty routine, Save has nothing to save, and New starts a routine that
     has already started. Hiding six useless buttons was the simple answer.

     It took the way in with them. Add a product is the one control that works
     on an empty routine, and it is how the routine stops being empty, so it
     stays and the other five go. The row keeps its place on the page rather
     than appearing once the first product lands, which also stops the page
     jumping under the shopper at the moment they add something.

     Shared mode keeps the old behaviour. A snapshot with nothing in it has no
     control that would do anything, Add a product included, because a shared
     routine is read-only. Its empty case is also usually a dead link rather
     than an empty routine, and fail() hides this row for that.

     Per-button hidden rather than a stylesheet rule, so the five that do not
     apply are out of the accessibility tree and out of the tab order instead
     of being invisible targets a keyboard still stops at. */
  function syncToolbar(count) {
    const empty = count === 0;

    if (isShared) {
      toolbarEl.hidden = empty;
      return;
    }

    toolbarEl.hidden = false;
    toolbarEl.classList.toggle("is-empty-routine", empty);

    toolbarEl.querySelectorAll("button, a").forEach((button) => {
      button.hidden = empty && button !== addProductButton;
    });
  }

  async function render() {
    const items = currentItems();
    const id = ++renderId;

    titleEl.textContent = currentName() || defaultTitle;
    renderNote();

    countEl.textContent = items.length
      ? `${items.length} ${items.length === 1 ? "product" : "products"}`
      : "";
    syncToolbar(items.length);
    if (!isShared) updateSaveLabel();

    /* LSC - The intro explains what this page is. That is worth three lines to
       someone who has just arrived at an empty one, and worth nothing to
       someone looking at a routine they built — who re-reads it on every visit
       and learns the same thing each time. On a phone those three lines are
       part of why the first product used to sit two screens down, so the copy
       now appears for the one visitor it actually helps. */
    if (introEl) introEl.hidden = items.length > 0;

    listEl.setAttribute("aria-busy", "true");

    /* LSC - A failed fetch used to leave the list empty and aria-busy="true"
       for good: render() was called bare from the change subscription and from
       boot(), neither of which caught, so one 5xx or one offline moment gave a
       shopper a permanent spinner and no explanation.

       The previous DOM is deliberately left alone on failure — a stale routine
       is more use than a blank one, and the next change re-renders. */
    let entries;
    try {
      entries = await R.fetchRows(items);
    } catch (err) {
      if (id !== renderId) return;
      listEl.setAttribute("aria-busy", "false");
      R.toast("Couldn't load your routine just now. Check your connection.", "error");
      return;
    }

    if (id !== renderId) return;

    const groups = R.groupByStep(entries, { includeEmpty: !isShared });

    listEl.textContent = "";
    listEl.setAttribute("aria-busy", "false");

    /* LSC - Every step renders, and the empty ones fold themselves.

       The problem this replaces: a nine-step scaffold with five "Nothing added
       yet." notices is useful onboarding while the routine is empty, and once
       there IS a routine the same five sections become the page describing its
       own schema instead of showing the shopper their products — around 450px
       of it on a phone, between the things they came to see.

       The old answer was to drop those sections and collect their links into
       one "Add to:" line at the end. That kept every destination reachable and
       cost a line instead of five sections, but it moved the way into a step
       away from the step itself, to the bottom of the page.

       The accordion gets both. Every step keeps its own place in the order,
       steps with products open, empty ones sit closed as a single row each,
       and opening one puts its way in exactly where that step belongs.

       Shared routines keep the old shape: groupByStep is called with
       includeEmpty: false for those, so there are no empty groups at all, and
       buildStep leaves a read-only snapshot without toggles. */
    groups.forEach((group) => listEl.appendChild(buildStep(group)));

    restoreHandleFocus();

    renderInteractionNotes(entries);
    if (!isShared) maybeOfferSave();
  }

  /* LSC - buildEmptySteps() lived here: the folded-away steps as one line,
     "Add to: Cleansers · Toners & Mists · …". The accordion replaced it, so
     each of those destinations is back inside the step it belongs to.

     Worth knowing if anything like it comes back: those links were the reason
     for an absolutely positioned ::after hit area rather than padding on the
     anchor. The theme's .link draws its underline as a background gradient at
     a fixed distance from the top of the box, so growing the box for a touch
     target pushes the text down through its own underline. The note is in
     claude/lsc-theme-link-underline-gotcha.md, and the same trap applies to
     any theme .link given a bigger tap area. */

  /* LSC - Which steps the shopper has opened or closed by hand, keyed by step
     label. Every change to the routine rebuilds the whole list, so without
     this a step collapsed by hand would spring open again the moment anything
     else on the page changed.

     A label is only in here once it has been clicked. Anything absent falls
     back to the default below, which is what makes "open if it has products"
     the starting state rather than a state that has to be written for all
     nine steps up front. */
  const stepOpenState = new Map();

  /* Counter behind each step body's id, which aria-controls on its toggle has
     to point at. It climbs across re-renders rather than resetting, so a stale
     node still in the document during a rebuild can never share an id with a
     fresh one. */
  let stepBodyId = 0;

  /* LSC - A two-line clamp with a More/Less button used to live here: it
     measured every blurb after render and grew a toggle for the rows whose
     text was cut. Removed, and nothing replaced it — no measuring, no resize
     listener, no per-row control.

     The usage instructions are what a routine is for. "Use in the evening
     after cleansing, two to three times per week" is the reason a shopper
     keeps the product in a step rather than in a cart, and asking for it a row
     at a time put an interaction in front of the page's own content. The
     height it bought went into the 44px touch targets in any case — about
     205px per row before the clamp, 203px after — so it was costing a control
     and a tap for roughly nothing. */

  /* LSC - The chevron that shows whether a step is open. Drawn here rather
     than in Liquid because the steps themselves are built in script, and
     rotated by CSS rather than swapped for a second glyph. */
  const CHEVRON_PATH = '<path d="M6 9l6 6 6-6"/>';

  function buildStep(group) {
    const meta = stepMeta.get(group.label) || { number: "", name: group.label, url: "" };
    const hasProducts = group.entries.length > 0;

    const section = R.el("section", "lsc-routine-step");
    section.dataset.step = group.label;
    if (!hasProducts) section.classList.add("is-empty");

    const head = R.el("div", "lsc-routine-step__head");
    const heading = R.el("h2", "lsc-routine-step__title");

    /* LSC - Teal, and only when the step holds something.

       The number is the one mark that reads at a glance down the left edge of
       the page, so it is what carries "this step is done" without a word for
       it. An empty step keeps the outlined number it always had.

       #487C7C rather than the brand's Soft Teal #7AA4A4: white on the brand
       value is 2.74:1, and this number is small text that has to be read, not
       a rule or a border. The darker shade is the same hue at 4.7:1, which
       clears AA. See the note in the colour standards at the top of
       assets/lsc-routine.css. */
    const number = R.el(
      "span",
      hasProducts ? "lsc-routine-step__number is-filled" : "lsc-routine-step__number",
      String(meta.number || "")
    );
    const name = R.el("span", "lsc-routine-step__name", meta.name || group.label);

    const body = R.el("div", "lsc-routine-step__items");

    /* LSC - An empty step says nothing about being empty.

       "Nothing added yet." used to sit here. It was telling a shopper what
       they could already see: the step is closed, and opening it shows a
       single line with no products under it. The one thing worth putting in
       there is the way in, which is the link below.

       The p is gone rather than hidden, so an empty step's body holds exactly
       one child and the flex gap has nothing to space against itself. */
    if (hasProducts) {
      group.entries.forEach((entry, i) => {
        body.appendChild(buildItem(entry, i, group.entries.length));
      });
    }

    /* LSC - The way into this step, inside the step.

       It used to sit in the head beside the title. It belongs in the body now,
       because the head is the toggle: a link next to a full-width button is
       both a tap target inside another tap target and a control that stays on
       screen when the thing it belongs to is closed. */
    if (!isShared && meta.url) {
      const link = R.el("a", "lsc-routine-step__link link text-sm", "Find products to add");
      link.href = meta.url;
      body.appendChild(link);
    }

    /* A shared routine is a read-only snapshot with no empty steps in it, so
       there is nothing for an accordion to fold. It keeps the plain heading it
       has always had. */
    if (isShared) {
      heading.append(number, name);
      head.appendChild(heading);
      section.append(head, body);
      return section;
    }

    /* LSC - The toggle is a real button inside the heading, which is the
       pattern assistive technology expects of an accordion: the heading still
       announces the step, and the button inside it announces expanded or
       collapsed and can be reached by keyboard without any key handling of
       our own. aria-controls needs an id, and the step label is not safe to
       put in one, so the index does it. */
    section.classList.add("is-collapsible");

    const bodyId = `LscRoutineStepBody-${stepBodyId++}`;
    body.id = bodyId;

    const open = stepOpenState.has(group.label)
      ? stepOpenState.get(group.label)
      : hasProducts;

    const toggle = R.el("button", "lsc-routine-step__toggle");
    toggle.type = "button";
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.setAttribute("aria-controls", bodyId);

    const chevron = R.el("span", "lsc-routine-step__chevron");
    chevron.setAttribute("aria-hidden", "true");
    chevron.innerHTML = iconSvg(CHEVRON_PATH);

    toggle.append(number, name, chevron);
    heading.appendChild(toggle);
    head.appendChild(heading);

    body.hidden = !open;
    section.classList.toggle("is-open", open);

    /* A listener per step rather than one delegated from the root: every
       change to the routine rebuilds this list, so these nodes and their
       listeners are discarded together and nothing accumulates. */
    toggle.addEventListener("click", () => {
      const next = body.hidden;
      body.hidden = !next;
      toggle.setAttribute("aria-expanded", next ? "true" : "false");
      section.classList.toggle("is-open", next);
      stepOpenState.set(group.label, next);
    });

    section.append(head, body);
    return section;
  }

  function buildItem(entry, index, total) {
    const { item, node } = entry;

    const wrapper = R.el(
      "div",
      isShared ? "lsc-routine-item lsc-routine-item--readonly" : "lsc-routine-item"
    );
    wrapper.dataset.variantGid = item.variant_gid;

    const body = node || fallbackRow(item);
    wrapper.appendChild(body);

    if (!isShared) {
      const slot = body.querySelector("[data-slot]") || body;
      slot.appendChild(controls(item, index, total));
    }

    const addOne = body.querySelector("[data-routine-add-one]");
    if (addOne) addOne.addEventListener("click", () => addOneToCart(addOne, item));

    return wrapper;
  }

  function fallbackRow(item) {
    const row = R.el("div", "lsc-routine-row__inner lsc-routine-row__inner--missing");
    const info = R.el("div", "lsc-routine-row__content");
    info.appendChild(R.el("p", "lsc-routine-row__title", item.handle || "This product"));
    info.appendChild(
      R.el(
        "p",
        "lsc-routine-row__unavailable text-xs",
        "This product is no longer available."
      )
    );
    const slot = R.el("div", "lsc-routine-row__slot");
    slot.setAttribute("data-slot", "");
    info.appendChild(slot);
    row.appendChild(info);
    return row;
  }

  /* LSC - Was an up arrow, a down arrow and a remove x. The arrows are gone;
     the order is changed by dragging a row to where it belongs.

     Two arrows per product meant two controls on every row to express one
     idea, and getting a product from the bottom of a step to the top took as
     many taps as there were products above it. A drag says the whole move in
     one gesture.

     The handle is a real <button>, not a div with a listener, because dragging
     is a pointer gesture and a pointer gesture alone would have deleted
     reordering for anyone using a keyboard. Arrow keys on a focused handle do
     exactly what the two arrow buttons used to do — see dragHandle(). */
  function controls(item, index, total) {
    const group = R.el("div", "lsc-routine-item__controls");

    const handle = dragHandle(item, index, total);

    const remove = iconButton("Remove from routine", "M18 6L6 18M6 6l12 12");
    remove.classList.add("lsc-routine-item__remove");

    /* LSC - Hand focus to a neighbouring row before the list is rebuilt.

       Reordering already restored focus; removing did not, so a keyboard user
       who deleted a product landed back on <body> and had to tab in from the
       top of the page again. The next row down is the natural place to be —
       it is the one that just moved into the gap — falling back to the one
       above when the last row in a step goes. */
    remove.addEventListener("click", () => {
      const row = remove.closest(".lsc-routine-item");
      const neighbour = row && (nextRow(row) || previousRow(row));
      if (neighbour) focusHandleAfterRender = neighbour.dataset.variantGid || "";
      R.remove(item.variant_gid);
    });

    group.append(handle, remove);
    return group;
  }

  const isRow = (n) => n && n.classList && n.classList.contains("lsc-routine-item");
  const nextRow = (row) => (isRow(row.nextElementSibling) ? row.nextElementSibling : null);
  const previousRow = (row) =>
    isRow(row.previousElementSibling) ? row.previousElementSibling : null;

  /* LSC - A four-way arrow, the "move me" mark. It was a six-dot grip, which
     is the convention for a list you drag vertically; the arrows say the same
     thing in a way that does not need the convention to be known. */
  const MOVE_PATHS =
    '<path d="M12 4v16M4 12h16"/>' +
    '<path d="M9.4 6.6L12 4l2.6 2.6M9.4 17.4L12 20l2.6-2.6"/>' +
    '<path d="M6.6 9.4L4 12l2.6 2.6M17.4 9.4L20 12l-2.6 2.6"/>';

  function dragHandle(item, index, total) {
    const button = R.el("button", "lsc-routine-icon-button lsc-routine-item__handle");
    button.type = "button";
    button.dataset.variantGid = item.variant_gid;

    /* The name says what BOTH input methods do, and the position is part of
       it: a screen reader user reordering by keyboard needs to know where the
       thing they are holding currently sits. */
    const label = `Reorder, ${index + 1} of ${total} in this step. Drag, or use the arrow keys.`;
    button.title = "Drag to reorder";
    button.setAttribute("aria-label", label);
    button.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" ' +
      'aria-hidden="true" focusable="false">' +
      MOVE_PATHS +
      "</svg>";

    /* A single item in a step has nothing to trade places with. The handle
       stays in the markup so the row's controls do not change shape from one
       step to the next, but it is inert and announces itself as such. */
    if (total < 2) {
      button.disabled = true;
      button.setAttribute("aria-label", "Reorder (only product in this step)");
      return button;
    }

    button.addEventListener("keydown", (e) => {
      const dir = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
      if (!dir) return;
      e.preventDefault();
      /* The move re-renders the list from scratch, so the button under the
         user's finger is about to be replaced by an identical one. Remember
         who to give focus back to. */
      focusHandleAfterRender = item.variant_gid;
      R.moveWithinStep(item.variant_gid, dir);
    });

    button.addEventListener("pointerdown", (e) => startDrag(e, button));

    return button;
  }

  function iconButton(label, path) {
    const button = R.el("button", "lsc-routine-icon-button");
    button.type = "button";
    button.title = label;
    button.setAttribute("aria-label", label);
    button.innerHTML = iconSvg(`<path d="${path}"/>`);
    return button;
  }

  /* ---------------------------------------------------------------------
     dragging a product into place

     LSC - Pointer events rather than HTML5 drag-and-drop. The native API does
     not fire on touch at all, and 80% of this shop's visitors are on a phone,
     so the one input method it supports is the one that matters least here.
     Pointer events are the same code for a finger, a mouse and a stylus.

     Reordering stays WITHIN a step, which is what the arrow buttons did and
     what the data model allows: an item's step comes from its own metafield,
     so dragging a serum into the cleanser step would be claiming something
     about the product rather than about this routine.
     --------------------------------------------------------------------- */

  /* Set before a reorder, read after the re-render that follows it: every
     mutation rebuilds the list, so the handle that was just used is about to
     be replaced by an identical one and focus would otherwise land on <body>. */
  let focusHandleAfterRender = "";
  let dragging = null;

  const rowsIn = (list) =>
    [...list.children].filter((n) => n.classList.contains("lsc-routine-item"));

  function restoreHandleFocus() {
    if (!focusHandleAfterRender) return;
    const gid = focusHandleAfterRender;
    focusHandleAfterRender = "";

    /* Matched by reading the dataset rather than by building a selector: a
       variant gid is "gid://shopify/ProductVariant/123", and the slashes and
       colons in it would need escaping to survive querySelector. */
    const row = [...listEl.querySelectorAll(".lsc-routine-item")].find(
      (n) => n.dataset.variantGid === gid
    );
    const handle = row && row.querySelector(".lsc-routine-item__handle");
    /* preventScroll because the page has not moved and the shopper's eyes are
       already on the row — jumping the viewport to re-show something that is
       already on screen reads as a glitch. */
    if (handle) handle.focus({ preventScroll: true });
  }

  function startDrag(e, handle) {
    if (dragging) return;
    if (typeof e.button === "number" && e.button > 0) return; // right/middle click

    const row = handle.closest(".lsc-routine-item");
    if (!row) return;

    const list = row.parentElement;
    const siblings = rowsIn(list);
    if (siblings.length < 2) return;

    /* Stops the browser selecting text and, with touch-action: none on the
       handle, stops the page scrolling under the finger instead of dragging. */
    e.preventDefault();

    const gid = handle.dataset.variantGid;
    const startIndex = siblings.indexOf(row);

    let pointerY = e.clientY;
    let originY = e.clientY;
    let offset = 0;
    let frame = 0;

    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* Pointer capture is a convenience, not a requirement — without it the
         events still arrive while the pointer is over the handle. */
    }

    row.classList.add("is-dragging");
    listEl.classList.add("is-reordering");

    const applyOffset = () => {
      row.style.transform = `translateY(${offset}px)`;
    };

    /* Move the row in the DOM without anything appearing to jump.

       For the dragged row: measure where it is, re-insert it, measure where it
       landed, and shift its transform by the difference so the pixels under
       the pointer do not move.

       For the row it traded places with: the same measurement, used the other
       way round. A DOM re-insert is a layout change, and layout changes do not
       transition — so the partner is put back where it was with a transform,
       and then released on the next frame, which the stylesheet's transition
       plays out as a slide. Without this the other row teleports. */
    const reinsert = (mutate, partner) => {
      const before = row.getBoundingClientRect().top;
      const partnerBefore = partner.getBoundingClientRect().top;

      row.style.transform = "";
      mutate();

      const after = row.getBoundingClientRect().top;
      offset = before - after;
      originY = pointerY - offset;
      applyOffset();

      const shift = partnerBefore - partner.getBoundingClientRect().top;
      if (!shift) return;

      partner.style.transition = "none";
      partner.style.transform = `translateY(${shift}px)`;
      requestAnimationFrame(() => {
        partner.style.transition = "";
        partner.style.transform = "";
      });
    };

    /* Swap with a neighbour once the dragged row's middle passes that
       neighbour's middle. Midpoints rather than edges, so a row does not
       flicker between two slots while it is half way across. */
    const maybeSwap = () => {
      const rect = row.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;

      const prev = row.previousElementSibling;
      if (prev && prev.classList.contains("lsc-routine-item")) {
        const r = prev.getBoundingClientRect();
        if (mid < r.top + r.height / 2) {
          reinsert(() => list.insertBefore(row, prev), prev);
          return;
        }
      }

      const next = row.nextElementSibling;
      if (next && next.classList.contains("lsc-routine-item")) {
        const r = next.getBoundingClientRect();
        if (mid > r.top + r.height / 2) {
          reinsert(() => list.insertBefore(row, next.nextSibling), next);
        }
      }
    };

    /* A step can be taller than a phone screen, and a finger holding still at
       the edge of one fires no further pointermove events — so the scrolling
       has to come from a frame loop rather than from the move handler. When
       the page scrolls under the row, the row's static position moves with it,
       so the origin is corrected by however much actually scrolled (which is
       not always what was asked for, at the ends of the document). */
    const EDGE = 72;
    const SPEED = 10;

    const tick = () => {
      frame = 0;
      if (!dragging) return;

      let step = 0;
      if (pointerY < EDGE) step = -SPEED;
      else if (pointerY > window.innerHeight - EDGE) step = SPEED;

      if (step) {
        const before = window.scrollY;
        window.scrollBy(0, step);
        const moved = window.scrollY - before;
        if (moved) {
          originY -= moved;
          offset = pointerY - originY;
          applyOffset();
          maybeSwap();
        }
      }

      frame = requestAnimationFrame(tick);
    };

    const onMove = (ev) => {
      pointerY = ev.clientY;
      offset = pointerY - originY;
      applyOffset();
      maybeSwap();
    };

    const onEnd = () => {
      if (!dragging) return;
      dragging = null;

      if (frame) cancelAnimationFrame(frame);
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onEnd);
      handle.removeEventListener("pointercancel", onEnd);
      document.removeEventListener("pointerup", onEnd);
      document.removeEventListener("pointercancel", onEnd);
      window.removeEventListener("blur", onEnd);
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        /* Already released, or never captured. */
      }

      /* Clear every inline transform, not just the dragged row's: a partner
         mid-slide when the pointer came up would otherwise be left holding one
         until the next render replaced it. */
      rowsIn(list).forEach((n) => {
        n.style.transform = "";
        n.style.transition = "";
      });
      row.classList.remove("is-dragging");
      listEl.classList.remove("is-reordering");

      const delta = rowsIn(list).indexOf(row) - startIndex;
      if (!delta) return;

      focusHandleAfterRender = gid;

      /* One moveWithinStep call per place moved, rather than writing a whole
         new order through replace(). replace() writes with { sync: false },
         hardcodes unsynced: false and drops source_id unless it is handed back
         — so a drag would quietly unbind a saved routine and tell the page it
         was synced when it was not. moveWithinStep is the exact path the two
         arrow buttons used, syncing included. A drag within one step is a
         handful of places at most, and each write re-renders from state, so
         the last render is the one that stands. */
      const dir = delta > 0 ? 1 : -1;
      for (let i = 0; i < Math.abs(delta); i++) R.moveWithinStep(gid, dir);
    };

    /* LSC - The end of a drag is also listened for on the document and on
       window blur, not just on the handle.

       `dragging` is the guard that stops a second drag starting, and it is
       cleared only in onEnd. If onEnd never ran, reordering was dead until a
       reload and the autoscroll frame loop kept running. Two ways that
       happened, both reachable: setPointerCapture throwing (it is in a
       try/catch, so a finger moving off the handle stopped delivering events
       and pointerup landed elsewhere), and a change from another tab
       re-rendering the list mid-drag, which destroys the handle and takes its
       listeners with it.

       onEnd is idempotent — it returns early when `dragging` is already
       null — so having it on three targets is free. */
    dragging = { gid };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onEnd);
    handle.addEventListener("pointercancel", onEnd);
    document.addEventListener("pointerup", onEnd);
    document.addEventListener("pointercancel", onEnd);
    window.addEventListener("blur", onEnd);
    frame = requestAnimationFrame(tick);
  }

  function renderInteractionNotes(entries) {
    R.renderInteractions(
      R.scanInteractions(
        entries.map((entry) => ({
          title:
            (entry.node && entry.node.dataset.title) ||
            entry.item.handle ||
            "This product",
          actives:
            entry.item.actives && entry.item.actives.length
              ? entry.item.actives
              : R.activesOf(entry.node),
        }))
      ),
      notesEl
    );
  }

  /* ---------------------------------------------------------------------
     name and note
     --------------------------------------------------------------------- */

  function renderNote() {
    if (!noteSection) return;
    const note = currentNote();

    /* LSC - The note's control is a pencil button now, so the words that used
       to be its label have to live somewhere. Two moves:

       - The empty state gets a muted placeholder in the text slot rather than
         an empty box. A square with a pencil in it, alone, does not say what
         it would edit.
       - The button's name goes in aria-label and title. Writing to
         textContent, as this did, would replace the SVG inside it. */
    if (noteText) {
      noteText.textContent = note || "No note yet.";
      noteText.classList.toggle("is-placeholder", !note);
      noteText.hidden = false;
    }

    if (noteEdit) {
      const label = note ? "Edit note" : "Add a note";
      noteEdit.setAttribute("aria-label", label);
      noteEdit.title = label;
    }

    noteSection.hidden = isShared && !note;
  }

  function rename() {
    askForText({
      title: "Name your routine",
      value: currentName() || "",
      placeholder: defaultTitle,
      maxLength: R.MAX_NAME,
      anchor: titleEl.closest(".lsc-routine__header"),
      onSubmit: (value) => R.setMeta({ name: R.cleanName(value) }),
    });
  }

  function editNote() {
    askForText({
      title: "A note for this routine",
      value: currentNote() || "",
      placeholder: "When to use it, what to watch for, anything worth remembering.",
      maxLength: R.MAX_NOTE,
      multiline: true,
      anchor: noteSection,
      onSubmit: (value) => R.setMeta({ note: R.cleanNote(value) }),
    });
  }

  /* ---------------------------------------------------------------------
     share links, cached

     Minting a snapshot is a network round trip and the Worker rate limits it,
     so Share, the QR code and every print must not each mint their own. The
     cache is keyed on the routine's content — products in order, name, note.
     Same content means the snapshot already out there still says the right
     thing and is handed back; change anything and the fingerprint stops
     matching, so the next request mints a fresh one rather than handing out a
     code that opens a routine the shopper has since edited.

     It lives under its own localStorage key because it is derived from the
     working copy rather than part of it: it must never ride along to the
     account, and losing it costs one round trip and nothing else.
     --------------------------------------------------------------------- */

  const SHARE_KEY = "laz_routine_share";

  /* Far inside the Worker's three-year share TTL — which itself resets every
     time the link is opened — so a cached link is re-minted long before the
     snapshot behind it could expire underneath it. This number is about
     content going stale, not about expiry. */
  const SHARE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  function shareFingerprint() {
    const state = R.read();
    return [
      state.items.map((i) => i.variant_gid).join("|"),
      state.name || "",
      state.note || "",
      /* LSC - Was a raw NUL byte in the source. Behaviourally identical, but a
         literal control character is the kind of thing an editor or a transport
         silently normalises away, and losing it here would quietly change every
         fingerprint and invalidate every cached share link. Written as an
         escape so it survives being moved around. */
    ].join("\u0000");
  }

  function cachedShare(fingerprint) {
    try {
      const entry = JSON.parse(window.localStorage.getItem(SHARE_KEY) || "null");
      if (!entry || typeof entry.url !== "string") return null;
      if (entry.fingerprint !== fingerprint) return null;
      if (Date.now() - (entry.created_at || 0) > SHARE_MAX_AGE_MS) return null;
      return entry.url;
    } catch {
      return null;
    }
  }

  async function shareUrl(options = {}) {
    /* A shared view is already sitting at its own permanent address. */
    if (isShared) return window.location.href;

    if (!R.count()) throw new Error("There's nothing in your routine to share yet.");

    const fingerprint = shareFingerprint();
    if (!options.force) {
      const hit = cachedShare(fingerprint);
      if (hit) return hit;
    }

    const res = await R.share();
    const url =
      res.url ||
      `${window.location.origin}${window.location.pathname}?token=${encodeURIComponent(res.token)}`;

    try {
      window.localStorage.setItem(
        SHARE_KEY,
        JSON.stringify({ fingerprint, url, created_at: Date.now() })
      );
    } catch {
      /* Private mode or a full quota. The link works; it just won't be reused. */
    }

    return url;
  }

  /* ---------------------------------------------------------------------
     print

     Built from the rendered page rather than from the stored items, because
     the DOM already holds the theme-rendered titles and variants — the
     alternative would be refetching every product row just to print. Written
     into a new tab as a standalone document so it carries no theme CSS and
     nothing on it depends on the storefront.

     Prices are deliberately left out: a printed routine is a reference for
     what to apply and in what order, and it outlives whatever the price was
     on the day it was printed.

     Empty steps DO appear here, unlike the earlier version. On screen an
     empty step is a prompt to go shopping; on paper it is a place to write in
     a product by hand, so each one gets its heading and a ruled line.

     The sheet ends with a QR code of the share link, which is what turns a
     printout back into something you can carry: scan it at the bathroom
     mirror and the routine opens on a phone.
     --------------------------------------------------------------------- */

  const escapeHtml = (value) =>
    String(value == null ? "" : value).replace(
      /[&<>"']/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );

  function textOf(scope, selector) {
    const node = scope.querySelector(selector);
    return node ? node.textContent.replace(/\s+/g, " ").trim() : "";
  }

  function collectForPrint() {
    const steps = [];

    listEl.querySelectorAll(".lsc-routine-step").forEach((section) => {
      const products = [...section.querySelectorAll(".lsc-routine-item")]
        .map((item) => ({
          title: textOf(item, ".lsc-routine-row__title"),
          variant: textOf(item, ".lsc-routine-row__variant"),
          blurb: textOf(item, ".lsc-routine-row__blurb"),
        }))
        .filter((p) => p.title);

      steps.push({
        number: textOf(section, ".lsc-routine-step__number"),
        name: textOf(section, ".lsc-routine-step__name"),
        products,
      });
    });

    const notes = [...notesEl.querySelectorAll(".lsc-routine-note")].map((li) => ({
      headline: textOf(li, ".lsc-routine-note__headline"),
      pairs: [...li.querySelectorAll(".lsc-routine-note__pair")].map((p) =>
        p.textContent.replace(/\s+/g, " ").trim()
      ),
      details: [...li.querySelectorAll(".lsc-routine-note__detail")].map((p) =>
        p.textContent.replace(/\s+/g, " ").trim()
      ),
    }));

    return { steps, notes };
  }

  /* Never throws: a printed sheet without a code is still a printed sheet, so
     an offline moment or a rate limit costs the QR and not the printout. */
  async function qrForPrint() {
    try {
      if (!window.LazQR) return null;
      const url = await shareUrl();
      if (!url) return null;
      return {
        url,
        svg: window.LazQR.svg(url, { quiet: 2, label: "QR code for this routine" }),
      };
    } catch {
      return null;
    }
  }

  function printDocument(data, qr) {
    const name = currentName() || defaultTitle;
    const note = currentNote();
    const printed = new Date().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const brand = printLogo
      ? `<img class="logo" src="${escapeHtml(printLogo)}" alt="${escapeHtml(shopName)}">`
      : `<span class="wordmark">${escapeHtml(shopName)}</span>`;

    const steps = data.steps
      .map(
        (step) => `
        <section class="step">
          <h2 class="step__title">
            <span class="step__number">${escapeHtml(step.number)}</span>
            <span>${escapeHtml(step.name)}</span>
          </h2>
          ${
            step.products.length
              ? `<ul class="products">
            ${step.products
              .map(
                (p) => `
              <li class="product">
                <span class="tick" aria-hidden="true"></span>
                <span class="product__body">
                  <span class="product__title">${escapeHtml(p.title)}${
                    p.variant
                      ? `<span class="product__variant"> - ${escapeHtml(p.variant)}</span>`
                      : ""
                  }</span>
                  ${p.blurb ? `<span class="product__blurb">${escapeHtml(p.blurb)}</span>` : ""}
                </span>
              </li>`
              )
              .join("")}
          </ul>`
              : `<div class="writein"><span class="tick" aria-hidden="true"></span><span class="writein__rule"></span></div>`
          }
        </section>`
      )
      .join("");

    const notes = data.notes.length
      ? `
      <section class="notes">
        <h2 class="notes__title">Using these together</h2>
        ${data.notes
          .map(
            (n) => `
          <div class="note">
            <p class="note__headline">${escapeHtml(n.headline)}</p>
            <ul class="note__pairs">
              ${n.pairs.map((p) => `<li>${escapeHtml(p)}</li>`).join("")}
            </ul>
            ${n.details.map((d) => `<p class="note__detail">${escapeHtml(d)}</p>`).join("")}
          </div>`
          )
          .join("")}
      </section>`
      : "";

    /* The SVG is generated here, not fetched, so it goes in as markup. */
    const qrBlockHtml = qr
      ? `
      <div class="colophon__qr">
        <div class="colophon__qr-code">${qr.svg}</div>
        <p class="colophon__qr-text">Scan to open this routine on your phone.</p>
      </div>`
      : "";

    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(name)} — ${escapeHtml(shopName)}</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 2.5rem 1.5rem 4rem;
    font: 15px/1.55 ui-sans-serif, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
    color: #1c1c1c;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .sheet { max-width: 46rem; margin: 0 auto; }
  .masthead {
    display: flex; align-items: flex-end; justify-content: space-between;
    gap: 1rem; padding-bottom: 0.9rem; margin-bottom: 1.6rem;
    border-bottom: 2px solid #1c1c1c;
  }
  /* The shop logo is a white-fill SVG built for a dark header, which would be
     invisible on paper. It is monochrome #ffffff with no currentColor, so
     inverting renders it solid black — the real mark, legible in print. */
  .logo { max-height: 21px; width: auto; display: block; filter: invert(1); }
  .wordmark { font-size: 1.1rem; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 600; }
  .masthead__meta { font-size: 0.78rem; color: #6a6a6a; text-align: right; white-space: nowrap; }
  h1 { font-size: 1.9rem; line-height: 1.15; margin: 0 0 0.35rem; }
  .subtitle { margin: 0 0 1.4rem; color: #6a6a6a; font-size: 0.9rem; }
  .routine-note {
    margin: 0 0 1.8rem; padding: 0.8rem 1rem;
    border-left: 3px solid #cfcfcf; background: #f7f7f7;
    white-space: pre-wrap; font-size: 0.92rem;
  }
  .step { margin-bottom: 1.5rem; break-inside: avoid; page-break-inside: avoid; }
  .step__title { display: flex; align-items: center; gap: 0.6rem; margin: 0 0 0.55rem; font-size: 1rem; }
  .step__number {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 1.6rem; height: 1.6rem; padding: 0 0.35rem;
    border: 1px solid #1c1c1c; border-radius: 50px;
    font-size: 0.78rem; font-weight: 600;
  }
  .products { list-style: none; margin: 0; padding: 0 0 0 2.2rem; }
  .product {
    display: flex; align-items: flex-start; gap: 0.7rem;
    padding: 0.45rem 0; border-bottom: 1px solid #ececec;
    break-inside: avoid; page-break-inside: avoid;
  }
  .tick {
    flex: 0 0 auto; width: 0.85rem; height: 0.85rem; margin-top: 0.25rem;
    border: 1px solid #9a9a9a; border-radius: 2px;
  }
  .product__body { flex: 1 1 auto; display: flex; flex-direction: column; }
  .product__title { font-weight: 600; }
  .product__variant { font-weight: 400; color: #6a6a6a; }
  .product__blurb { font-size: 0.82rem; color: #4a4a4a; margin-top: 0.15rem; }
  /* An unfilled step: somewhere to write a product in by hand. */
  .writein {
    display: flex; align-items: center; gap: 0.7rem;
    margin-left: 2.2rem; padding: 0.45rem 0;
  }
  .writein__rule { flex: 1 1 auto; height: 1.35rem; border-bottom: 1px solid #c9c9c9; }
  .notes { margin-top: 2rem; padding-top: 1.1rem; border-top: 1px solid #d8d8d8; break-inside: avoid; }
  .notes__title { font-size: 1rem; margin: 0 0 0.8rem; }
  .note { margin-bottom: 0.9rem; padding-left: 0.75rem; border-left: 3px solid #cfcfcf; break-inside: avoid; }
  .note__headline { margin: 0 0 0.15rem; font-weight: 600; font-size: 0.92rem; }
  .note__pairs { list-style: none; margin: 0 0 0.25rem; padding: 0; font-size: 0.78rem; color: #6a6a6a; }
  .note__detail { margin: 0; font-size: 0.86rem; }
  .colophon {
    margin-top: 2.5rem; padding-top: 1rem; border-top: 1px solid #d8d8d8;
    font-size: 0.82rem; color: #6a6a6a;
    display: flex; justify-content: space-between; align-items: flex-end;
    gap: 1.5rem; flex-wrap: wrap;
    break-inside: avoid; page-break-inside: avoid;
  }
  .colophon a { color: #1c1c1c; }
  .colophon__brand { display: flex; flex-direction: column; gap: 0.15rem; }
  .colophon__qr { display: flex; align-items: center; gap: 0.75rem; }
  .colophon__qr-code { flex: 0 0 auto; width: 96px; height: 96px; }
  .colophon__qr-code svg { display: block; width: 100%; height: 100%; }
  .colophon__qr-text { margin: 0; max-width: 8.5rem; font-size: 0.78rem; }
  .toolbar { margin: 0 auto 1.5rem; max-width: 46rem; }
  .toolbar button {
    font: inherit; font-size: 0.85rem; padding: 0.45rem 1.1rem; cursor: pointer;
    border: 1px solid #1c1c1c; border-radius: 4px; background: #1c1c1c; color: #fff;
  }
  @media print {
    body { padding: 0; }
    .toolbar { display: none; }
    a { text-decoration: none; color: inherit; }
  }
  @page { margin: 16mm; }
</style>
</head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Print this routine</button></div>

  <div class="sheet">
    <header class="masthead">
      ${brand}
      <div class="masthead__meta">Printed ${escapeHtml(printed)}</div>
    </header>

    <h1>${escapeHtml(name)}</h1>
    <p class="subtitle">Apply in the order shown.</p>

    ${note ? `<p class="routine-note">${escapeHtml(note)}</p>` : ""}

    ${steps}
    ${notes}

    <footer class="colophon">
      <div class="colophon__brand">
        <span>${escapeHtml(shopName)}</span>
        <a href="${escapeHtml(shopUrl)}">${escapeHtml(shopUrl.replace(/^https?:\/\//, ""))}</a>
      </div>
      ${qrBlockHtml}
    </footer>
  </div>

  <script>
    /* Wait for the logo so it is not missing from the printed sheet. */
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 250);
    });
  <\/script>
</body>
</html>`;
  }

  /* Shown while the share link is minted, so the new tab is never blank. */
  function waitingDocument() {
    return `<!doctype html>
<html lang="en">
<head><meta charset="utf-8"><title>Preparing your routine…</title>
<style>
  body {
    margin: 0; height: 100vh; display: flex; align-items: center; justify-content: center;
    font: 15px/1.5 ui-sans-serif, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
    color: #6a6a6a; background: #fff;
  }
</style>
</head>
<body>Preparing your routine…</body>
</html>`;
  }

  function writeInto(win, html) {
    win.document.open();
    win.document.write(html);
    win.document.close();
  }

  function openPrintView() {
    const data = collectForPrint();

    /* Steps always exist; products may not. Only refuse when the routine is
       genuinely empty, since a blank sheet of headings is still useful. */
    const hasProducts = data.steps.some((s) => s.products.length);
    if (!data.steps.length || (!hasProducts && !currentItems().length && isShared)) {
      R.toast("There's nothing in this routine to print yet.", "error");
      return;
    }

    /* The window has to be opened inside the click, before any await, or the
       pop-up blocker takes it. The share link is fetched afterwards and the
       document rewritten in place. */
    const win = window.open("", "_blank");
    if (!win) {
      R.toast("Your browser blocked the print window. Allow pop-ups and try again.", "error");
      return;
    }

    writeInto(win, waitingDocument());

    qrForPrint().then((qr) => {
      if (win.closed) return;
      writeInto(win, printDocument(data, qr));
    });
  }

  /* ---------------------------------------------------------------------
     cart
     --------------------------------------------------------------------- */

  async function addOneToCart(button, item) {
    await withBusy(button, async () => {
      try {
        await R.addToCart([item]);
        R.toast("Added to your cart.", "success");
      } catch (err) {
        R.toast(err.message, "error");
      }
    });
  }

  async function addAllToCart(button) {
    const items = currentItems();
    if (!items.length) return;

    await withBusy(button, async () => {
      try {
        await R.addToCart(items);
        R.toast(
          `Added ${items.length} ${items.length === 1 ? "product" : "products"} to your cart.`,
          "success"
        );
      } catch (err) {
        R.toast(
          `${err.message} Nothing was added — remove any unavailable product and try again.`,
          "error"
        );
      }
    });
  }

  function setChildren(node, children) {
    while (node.firstChild) node.removeChild(node.firstChild);
    children.forEach((child) => node.appendChild(child));
  }

  /* Swaps a button's label for a busy one and puts it back afterwards.

     It keeps the original CHILD NODES rather than the label's text. Saving
     textContent and restoring it as a string looks equivalent and is not:
     every toolbar button's label contains an icon, and the restore flattens
     the markup to a plain string, so the icon disappears on first use and
     never comes back until the page reloads.

     The restore is also conditional, because a save re-renders the toolbar
     mid-flight and an unconditional restore would put the stale label back. */
  async function withBusy(button, fn) {
    if (!button) return fn();

    /* LSC - An icon-only toolbar button has no room for the word "Working…",
       and swapping a 19px glyph for nine characters of text would resize the
       button and reflow the whole row around it — six buttons jumping sideways
       because one of them is thinking.

       Those buttons carry .lsc-routine__toolbar-btn--icon and get a busy
       STATE rather than a busy label: disabled so it cannot be pressed twice,
       aria-busy so a screen reader hears that something is in flight, and a
       class the stylesheet dims. The glyph stays put and the row does not
       move. */
    if (button.classList.contains("lsc-routine__toolbar-btn--icon")) {
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.classList.add("is-busy");
      try {
        return await fn();
      } finally {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        button.classList.remove("is-busy");
      }
    }

    const label = button.querySelector(".btn-text") || button;
    const original = Array.prototype.slice.call(label.childNodes);

    button.disabled = true;
    setChildren(label, [document.createTextNode(BUSY_LABEL)]);

    try {
      await fn();
    } finally {
      button.disabled = false;
      if (label.textContent === BUSY_LABEL) setChildren(label, original);
    }
  }

  /* ---------------------------------------------------------------------
     sharing

     One panel, one snapshot, two forms of the same link. The QR code used to
     have its own button and its own panel, both minting from the same cached
     share URL — so the only thing the second control bought was the chance to
     leave a stale code sitting in a closed panel while the open one showed a
     newer link.
     --------------------------------------------------------------------- */

  /* Every panel that competes for the same space carries data-routine-panel.
     Opening one closes the rest. The search panel is built by another script
     and is closed by this the same as any other, which is why the convention
     lives in the markup rather than in either script's imports. */
  function openPanel(panel) {
    root.querySelectorAll("[data-routine-panel]").forEach((node) => {
      if (node !== panel) node.hidden = true;
    });
    if (panel) panel.hidden = false;
  }

  /* LSC - Shutting the share panel is now something this script does as well
     as opening it: the toolbar button toggles, and the panel has an x of its
     own. restoreFocus is for the two deliberate closes, so focus does not get
     dropped at the top of the document. */
  function closeSharePanel(options) {
    if (!sharePanel) return;
    sharePanel.hidden = true;
    if (options && options.restoreFocus && shareToggle) shareToggle.focus();
  }

  /* LSC - aria-expanded has to follow the panel whoever hid it: the x, the
     toolbar button, or assets/lsc-routine-search.js opening ITS panel over
     this one. An observer on the hidden attribute means none of those paths
     has to remember to announce it - including the one in another file, which
     this script deliberately knows nothing about. */
  if (sharePanel && shareToggle) {
    const syncShareExpanded = () =>
      shareToggle.setAttribute("aria-expanded", sharePanel.hidden ? "false" : "true");

    new MutationObserver(syncShareExpanded).observe(sharePanel, {
      attributes: true,
      attributeFilter: ["hidden"],
    });

    syncShareExpanded();

    /* Escape closes from inside the panel, the same as the product search
       panel beside it. */
    sharePanel.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      closeSharePanel({ restoreFocus: true });
    });
  }

  /* ---------------------------------------------------------------------
     the toolbar

     LSC - There is no overflow menu any more. Share, Print, New routine and
     (for a signed-in shopper) Save used to live in a wrapper below the row,
     revealed by a "…" button; they are buttons in the row itself now, most of
     them icon-only. Everything that managed that menu - the nullable
     moreGroup/moreToggle lookups, closeMoreMenu(), the MutationObserver that
     kept aria-expanded honest, the Escape handler and the click-outside
     listener - went with it rather than being left guarded and dead.

     If a menu ever comes back, note what it cost to keep correct: four
     separate code paths could hide that group, and each one had to be stopped
     from lying about aria-expanded.
     --------------------------------------------------------------------- */

  /* Never throws. A code is a convenience on top of the link; a browser where
     lsc-qr.js failed, or a string QR cannot encode, must still get a share
     link rather than an error where the panel should be. */
  function renderShareQr(url) {
    if (!qrBlock || !qrCode) return;
    try {
      if (!window.LazQR) throw new Error("no encoder");
      /* Generated markup, not fetched — nothing here came from a user. */
      qrCode.innerHTML = window.LazQR.svg(url, {
        quiet: 2,
        label: "QR code for this routine",
      });
      qrBlock.hidden = false;
    } catch {
      qrCode.textContent = "";
      qrBlock.hidden = true;
    }
  }

  async function share(button) {
    /* LSC - A toggle. The button used to only ever open, so a second press
       re-minted a snapshot of a routine that had not changed and re-opened a
       panel that was already there. Open panel, press again, it closes - and
       nothing is minted.

       The close is checked before withBusy so shutting the panel never waits
       on a request or flashes "Working..." on the button. */
    if (sharePanel && !sharePanel.hidden) {
      closeSharePanel({ restoreFocus: true });
      return;
    }

    await withBusy(button, async () => {
      try {
        const url = await shareUrl();
        openPanel(sharePanel);
        shareInput.value = url;
        renderShareQr(url);
        shareInput.focus();
        shareInput.select();
      } catch (err) {
        R.toast(err.message, "error");
      }
    });
  }

  async function copyLink(input) {
    const text = input && input.value;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      R.toast("Link copied.", "success");
    } catch {
      input.focus();
      input.select();
      R.toast("Press ⌘C / Ctrl+C to copy the link.", "info");
    }
  }

  /* ---------------------------------------------------------------------
     saving
     --------------------------------------------------------------------- */

  function setSaveIcon(name) {
    if (saveIcon) saveIcon.innerHTML = iconSvg(ICONS[name] || ICONS.save);
  }

  /* LSC - The Save button is icon only in the toolbar now, so this text is no
     longer on screen: the label span is visually hidden and exists for the
     screen reader, which is the one reader that would otherwise learn nothing
     from a bookmark turning into a tick.

     The same words go onto title and aria-label. title is the hover, and
     aria-label is what actually gets announced — a button with an aria-label
     has its inner text ignored, so leaving aria-label on the markup's static
     "Save to my account" would have made this whole function silent to the
     people it is written for. */
  function setSaveText(text) {
    if (saveLabel) saveLabel.textContent = text;
    if (saveButton) {
      saveButton.title = text;
      saveButton.setAttribute("aria-label", text);
    }
  }

  function updateSaveLabel() {
    if (!saveLabel && !saveButton) return;
    const state = R.read();

    if (!state.source_id) {
      setSaveText("Save to my account");
      setSaveIcon("save");
      if (saveButton) saveButton.classList.remove("is-sync-error");
      return;
    }

    const sync = R.syncState();
    const labels = { saving: "Saving…", pending: "Saving…", error: "Retry save" };
    const text = labels[sync] || (state.unsynced ? "Retry save" : "Saved");

    setSaveText(text);
    setSaveIcon(text === "Saved" ? "saved" : text === "Retry save" ? "retry" : "save");

    if (saveButton) {
      saveButton.classList.toggle("is-sync-error", sync === "error" || state.unsynced);
    }
  }

  /* LSC - "New routine", formerly "Clear".

     The old handler was one line: confirm, then clear. That asked the same
     question whether the routine was safely on the account or existed nowhere
     but this browser, which meant the warning carried no information — and the
     one case that genuinely loses work got no more weight than the case that
     loses nothing.

     Four situations, and only two of them deserve a question:

       empty            nothing to lose; just unbind and go.
       bound, unsynced  the account is the safety net but has not caught up.
                        Land the changes first and only ask if that fails,
                        which is the same order switchTo() uses in
                        assets/lsc-routine-button.js.
       bound, synced    the saved routine survives this. Say where it went
                        rather than asking permission to do something safe.
       not bound        THIS is the warning. The routine exists in this browser
                        and nowhere else, and clearing is the end of it.

     options.fromLink marks an arrival from the saved-routines page's "New
     routine" button, which lands here with ?new=1 rather than clearing before
     it navigates. Doing it here keeps one implementation of this check instead
     of a second copy in assets/lsc-my-routines.js, and it means the question
     is asked with the routine in question on screen behind it. */
  async function startNewRoutine(options = {}) {
    const state = R.read();
    const named = state.name ? `“${state.name}”` : "your current routine";

    if (!state.items.length) {
      /* Still worth clearing when bound: an empty working copy pointing at a
         saved routine would write that emptiness back on the next edit. */
      if (state.source_id) await R.clear();
      return;
    }

    if (state.source_id) {
      if (state.unsynced) {
        const flushed = await R.syncNow();
        if (!flushed) {
          const ok = window.confirm(
            `Changes to ${named} haven't reached your account yet. Start a new routine anyway and discard them?`
          );
          if (!ok) return keptCurrent(options);
        }
      }
      await R.clear();
      R.toast(`Started a new routine. ${named} is still in your saved routines.`, "success");
      return;
    }

    /* Unbound: nothing anywhere else holds this. */
    const warning = isCustomer
      ? `${named} isn't saved to your account yet, so starting a new routine will discard it. Save it first with the Save button, or start fresh now?`
      : `${named} is only saved in this browser, so starting a new routine will discard it. Log in first to keep it, or start fresh now?`;

    if (!window.confirm(warning)) return keptCurrent(options);

    await R.clear();
    R.toast("Started a new routine.", "success");
  }

  /* Declining is a real outcome, not a no-op — especially when the decline
     followed a navigation, where saying nothing would look like the button on
     the previous page had simply failed. */
  function keptCurrent(options) {
    if (options.fromLink) {
      R.toast("Kept the routine you were working on.", "info");
    }
  }

  async function save(button) {
    const state = R.read();
    if (!state.items.length) return;

    if (state.source_id) {
      const ok = await R.syncNow();
      R.toast(
        ok ? `Saved to “${state.name || "your routine"}”.` : "We couldn't reach your account.",
        ok ? "success" : "error"
      );
      return;
    }

    const create = async (name) => {
      await withBusy(button, async () => {
        try {
          const res = await R.createRoutine({ name, note: state.note, items: state.items });
          const saved = res.routine || {};
          /* Awaited: R.replace() flushes pending work before it rebinds, so it
             is async now, and the toast below should follow the rebind. */
          await R.replace(state.items, {
            source_id: saved.id,
            name: saved.name || name,
            note: state.note,
          });
          R.toast(`Saved “${saved.name || name}”.`, "success");
          offerMyRoutines();
        } catch (err) {
          handleSaveError(err);
        }
      });
      updateSaveLabel();
    };

    if (state.name) return create(state.name);

    askForText({
      title: "Name this routine",
      value: "",
      placeholder: "Morning",
      maxLength: R.MAX_NAME,
      anchor: toolbarEl,
      onSubmit: (value) => {
        const name = R.cleanName(value);
        if (!name) return;
        R.setMeta({ name });
        create(name);
      },
    });
  }

  async function saveCopy(button) {
    const items = currentItems();
    if (!items.length) return;

    askForText({
      title: "Name your copy",
      value: currentName() || "Shared routine",
      maxLength: R.MAX_NAME,
      anchor: toolbarEl,
      onSubmit: async (value) => {
        const name = R.cleanName(value);
        if (!name) return;
        await withBusy(button, async () => {
          try {
            const res = await R.createRoutine({ name, note: currentNote(), items });
            const saved = res.routine || {};
            R.toast(`Saved “${saved.name || name}” to your account.`, "success");
            button.hidden = true;
            offerMyRoutines();
          } catch (err) {
            handleSaveError(err);
          }
        });
      },
    });
  }

  function offerMyRoutines() {
    if (root.querySelector(".lsc-routine-saved-link")) return;
    const p = R.el("p", "lsc-routine-saved-link text-sm");
    const a = R.el("a", "link", "See it in my routines");
    a.href = myRoutinesUrl;
    p.appendChild(a);
    toolbarEl.insertAdjacentElement("afterend", p);
  }

  function handleSaveError(err) {
    if (err.code === "too_large" || err.status === 413) {
      R.toast(
        "Your saved routines are full. Delete one in My routines to make room, then save again.",
        "error"
      );
      return;
    }
    if (err.code === "too_many" || err.status === 409) {
      R.toast(err.message, "error");
      return;
    }
    if (err.status === 401 || err.status === 403) {
      R.toast("Your session expired. Log in again to save.", "error");
      return;
    }
    R.toast(err.message, "error");
  }

  function askForText(options) {
    const existing = root.querySelector(".lsc-routine-name-form");
    if (existing) existing.remove();

    const form = R.el("form", "lsc-routine-name-form");
    const field = options.multiline
      ? R.el("textarea", "input lsc-routine-name-form__textarea")
      : R.el("input", "input lsc-routine-name-form__input");

    if (!options.multiline) field.type = "text";
    if (options.multiline) field.rows = 4;
    field.value = options.value || "";
    field.maxLength = options.maxLength || 200;
    if (options.placeholder) field.placeholder = options.placeholder;
    field.id = `lsc-routine-field-${Date.now()}`;

    const label = R.el("label", "lsc-routine-name-form__label text-sm", options.title);
    label.setAttribute("for", field.id);

    const submit = R.el("button", "button button--primary", "Save");
    submit.type = "submit";
    const cancel = R.el("button", "lsc-routine-text-button link text-sm", "Cancel");
    cancel.type = "button";
    cancel.addEventListener("click", () => form.remove());

    const actions = R.el("div", "lsc-routine-name-form__actions");
    actions.append(submit, cancel);
    form.append(label, field, actions);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const value = field.value;
      form.remove();
      options.onSubmit(value);
    });

    (options.anchor || root).insertAdjacentElement("afterend", form);
    field.focus();
    if (!options.multiline) field.select();
  }

  function maybeOfferSave() {
    if (!isCustomer || !R.count() || R.read().source_id) return;
    if (root.querySelector(".lsc-routine-offer")) return;

    let done = false;
    try {
      done = window.localStorage.getItem(PROMPT_KEY) === "1";
    } catch {
      done = true;
    }
    if (done) return;

    const banner = R.el("div", "lsc-routine-offer");
    banner.appendChild(
      R.el(
        "p",
        "lsc-routine-offer__text text-sm",
        "You have a routine in this browser. Save it to your account to keep it."
      )
    );

    const yes = R.el("button", "button button--primary", "Save it");
    yes.type = "button";
    yes.addEventListener("click", () => {
      dismissOffer(banner);
      save(saveButton);
    });

    const no = R.el("button", "lsc-routine-text-button link text-sm", "Not now");
    no.type = "button";
    no.addEventListener("click", () => dismissOffer(banner));

    const actions = R.el("div", "lsc-routine-offer__actions");
    actions.append(yes, no);
    banner.appendChild(actions);

    toolbarEl.insertAdjacentElement("afterend", banner);
  }

  function dismissOffer(banner) {
    try {
      window.localStorage.setItem(PROMPT_KEY, "1");
    } catch {
      /* nothing to remember it with — the offer returns next visit */
    }
    banner.remove();
  }

  /* ---------------------------------------------------------------------
     wiring
     --------------------------------------------------------------------- */

  root.addEventListener("click", (e) => {
    const addAll = e.target.closest("[data-routine-add-all]");
    if (addAll) return addAllToCart(addAll);

    /* Printing works in both modes, so it sits above the shared-mode guard.
       The QR code is no longer a control of its own — it is rendered into the
       share panel by share(), and onto the printed sheet by qrForPrint(). */
    const printBtn = e.target.closest("[data-routine-print]");
    if (printBtn) return openPrintView();

    /* LSC - Only a signed-in shopper's copy button carries this attribute. A
       guest gets a link to the login page in the same slot, deliberately
       without it: with the attribute, this branch would open a name prompt on
       the way out of the page, for a copy that cannot be written.

       The .disabled check is what withBusy() leaves behind while a save is in
       flight — the double-click guard, not a mode guard. */
    const saveCopyBtn = e.target.closest("[data-routine-save-copy]");
    if (saveCopyBtn) {
      if (saveCopyBtn.disabled) return;
      return saveCopy(saveCopyBtn);
    }

    /* LSC - Sharing works in both modes, so all three of its controls sit
       above the shared-mode guard: the toolbar button, the panel's x, and the
       Copy button inside the panel.

       All three used to be below it. Moving only the toolbar button would have
       given a visitor a panel they could open, copy nothing from, and not
       close — the two controls inside the panel are as much a part of sharing
       as the one that opens it.

       Nothing is minted here for a visitor. share() goes through shareUrl(),
       whose first line returns window.location.href when isShared, so the link
       handed to the panel is the one the visitor arrived on.

       The "…" toggle used to be handled here too, and the reason it was moved
       is the same reason these are: that branch originally sat at the BOTTOM
       of this handler, under the guard, which meant the one mode where the
       menu was the only way to reach Print was the one mode where pressing it
       did nothing. Anything added below that line is own-mode whether or not
       anyone decided it should be, so decide which side a new branch belongs
       on before adding it. */
    const shareBtn = e.target.closest("[data-routine-share]");
    if (shareBtn) return share(shareBtn);

    /* The panel's own x. A separate attribute from the toolbar button, so
       neither selector can match the other element. */
    const shareCloseBtn = e.target.closest("[data-routine-share-close]");
    if (shareCloseBtn) return closeSharePanel({ restoreFocus: true });

    const copyBtn = e.target.closest("[data-routine-share-copy]");
    if (copyBtn) return copyLink(shareInput);

    if (isShared) return;

    const renameBtn = e.target.closest("[data-routine-rename]");
    if (renameBtn) return rename();

    const noteBtn = e.target.closest("[data-routine-note-edit]");
    if (noteBtn) return editNote();

    const newBtn = e.target.closest("[data-routine-new]");
    if (newBtn) return startNewRoutine();

    const saveBtn = e.target.closest("[data-routine-save]");
    if (saveBtn) return save(saveBtn);
  });

  boot();
})();
